import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  readSelectedProjects, 
  cacheSelectedProjects 
} from '../../utils/influencerContractCache';
import { warmResultPendingProjects } from '../../utils/influencerResultCache';
import ProjectDetailModal from '../marketer/project/modals/ProjectDetailModal';
import ContractRequestModal from './project/modals/ContractRequestModal';
import ContractGuideModal from './project/modals/ContractGuideModal';
import ContractConfirmModal from './project/modals/ContractConfirmModal';
import ContractCompleteModal from './project/modals/ContractCompleteModal';
import ContractRejectModal from './project/modals/ContractRejectModal';
import ContractRejectCompleteModal from './project/modals/ContractRejectCompleteModal';
import '../../styles/common/ib-layout.css';
import '../../styles/common/ib-forms.css';

function normalizeAccountName(raw) {
  if (!raw) return raw;
  const s = String(raw).trim();
  if (!s.toLowerCase().startsWith('http')) return s;
  try {
    const segment = new URL(s).pathname.replace(/^\//, '').replace(/^@/, '').split('/')[0];
    return segment || s;
  } catch {
    return s;
  }
}

const preloadImage = (url) => {
  if (!url) return;
  const img = new Image();
  img.src = url;
};

const calculateDday = (expireDate) => {
  if (!expireDate) return null;
  const now = new Date();
  const expire = new Date(expireDate);
  const diff = Math.ceil((expire - now) / (1000 * 60 * 60 * 24));
  return diff;
};

function InfluencerProjectContract() {
  const [uid, setUid] = useState(window.__ME__?.id);
  
  useEffect(() => {
    const currentUid = window.__ME__?.id;
    if (currentUid !== uid) {
      setUid(currentUid);
    }
    
    const handleUserReady = (event) => {
      const userId = event.detail?.id;
      if (userId) {
        setUid(userId);
      }
    };
    
    if (!window.__USER_READY_LISTENERS__) {
      window.__USER_READY_LISTENERS__ = [];
    }
    window.__USER_READY_LISTENERS__.push(handleUserReady);
    
    return () => {
      if (window.__USER_READY_LISTENERS__) {
        const idx = window.__USER_READY_LISTENERS__.indexOf(handleUserReady);
        if (idx > -1) {
          window.__USER_READY_LISTENERS__.splice(idx, 1);
        }
      }
    };
  }, [uid]);
  
  const initialProjects = useMemo(() => {
    return uid ? (readSelectedProjects(uid) || []) : [];
  }, [uid]);
  
  const hasCache = uid && initialProjects.length > 0;
  
  const [projects, setProjects] = useState(initialProjects);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(!hasCache);
  const [error, setError] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  const [contractStep, setContractStep] = useState(0);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRejectCompleteModal, setShowRejectCompleteModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  
  const currentProject = projects[currentIndex] || null;
  
  const fetchProjects = useCallback(async () => {
    if (!uid) return;
    
    const cached = readSelectedProjects(uid);
    if (cached) {
      setProjects(cached);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch('/api/influencer/mybiz/projects/selected', {
        credentials: 'include'
      });
      const data = await res.json();
      
      if (!res.ok || !data.ok) {
        throw new Error(data.message || '프로젝트 목록 조회 실패');
      }
      
      setProjects(data.projects || []);
      cacheSelectedProjects(uid, data.projects || []);
      
      if (data.projects && data.projects.length > 0 && data.projects[0].coverImage) {
        preloadImage(data.projects[0].coverImage);
      }
      
      if (data.projects && data.projects.length > 1 && data.projects[1].coverImage) {
        setTimeout(() => preloadImage(data.projects[1].coverImage), 100);
      }
      
    } catch (e) {
      console.error('프로젝트 목록 조회 오류:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [uid]);
  
  useEffect(() => {
    if (uid && !hasCache) {
      fetchProjects();
    }
  }, [uid, hasCache, fetchProjects]);
  
  const getServiceTypeText = (type) => {
    const map = {
      delivery: '제품 제공',
      purchase: '제품 미제공',
      visit: '장소 광고',
      free: '광고주 자율방식',
      custom: '광고주 자율방식'
    };
    return map[type] || type;
  };
  
  const getContentTypeText = (type) => {
    const map = {
      post: '포스트(피드)',
      video: '영상(릴스/쇼츠)',
      free: '자유'
    };
    return map[type] || type;
  };
  
  const getProductName = (project) => {
    if (!project) return '광고 프로젝트';
    
    if (project.serviceType === 'delivery' || project.serviceType === 'purchase') {
      return project.product?.name || '광고 프로젝트';
    } else if (project.serviceType === 'visit') {
      return project.product?.placeName || '광고 프로젝트';
    } else if (project.serviceType === 'free' || project.serviceType === 'custom') {
      return project.product?.customTitle || '광고 프로젝트';
    }
    return '광고 프로젝트';
  };
  
  const handleAcceptContract = useCallback(async () => {
    if (!currentProject || !uid) return;
    
    try {
      const res = await fetch(
        `/api/influencer/mybiz/projects/${currentProject.id}/contract/accept`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            applicationId: currentProject.applicationId
          })
        }
      );
      
      const data = await res.json();
      
      if (!res.ok || !data.ok) {
        const errorMsg = data.displayMessage || data.message || '계약 수락에 실패했습니다.';
        alert(errorMsg);
        return;
      }
      
      setContractStep(0);
      setShowCompleteModal(true);
      
      if (uid) {
        try {
          const keys = Object.keys(sessionStorage);
          keys.forEach(key => {
            if (key.startsWith(`INF_LIST:${uid}:`)) {
              sessionStorage.removeItem(key);
            }
          });
          
          if (window.warmupInfluencer) {
            window.warmupInfluencer()
              .then(() => {
                window.dispatchEvent(new CustomEvent('project-list-update'));
              })
              .catch(() => {
                window.dispatchEvent(new CustomEvent('project-list-update'));
              });
          }
          
          warmResultPendingProjects(uid).catch(() => {});
        } catch (e) {
          console.error('캐시 무효화 실패:', e);
        }
      }
      
    } catch (e) {
      console.error('계약 수락 오류:', e);
      alert('네트워크 오류가 발생했습니다.');
    }
  }, [currentProject, uid]);
  
  const handleCompleteModalClose = useCallback(() => {
    setShowCompleteModal(false);
    
    const newProjects = projects.filter((_, idx) => idx !== currentIndex);
    setProjects(newProjects);
    cacheSelectedProjects(uid, newProjects);
    
    if (newProjects.length === 0) {
      setCurrentIndex(0);
    } else if (currentIndex >= newProjects.length) {
      setCurrentIndex(newProjects.length - 1);
    }
  }, [projects, currentIndex, uid]);
  
  const handleRejectContract = useCallback(async (reason) => {
    if (!currentProject || !uid) return;
    
    try {
      const res = await fetch(
        `/api/influencer/mybiz/projects/${currentProject.id}/contract/reject`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            applicationId: currentProject.applicationId,
            rejectionReason: reason || ''
          })
        }
      );
      
      const data = await res.json();
      
      if (!res.ok || !data.ok) {
        const errorMsg = data.displayMessage || data.message || '계약 거절에 실패했습니다.';
        alert(errorMsg);
        return;
      }
      
      setShowRejectModal(false);
      setShowRejectCompleteModal(true);
      
      if (uid) {
        try {
          const keys = Object.keys(sessionStorage);
          keys.forEach(key => {
            if (key.startsWith(`INF_LIST:${uid}:`)) {
              sessionStorage.removeItem(key);
            }
          });
          
          if (window.warmupInfluencer) {
            window.warmupInfluencer()
              .then(() => {
                window.dispatchEvent(new CustomEvent('project-list-update'));
              })
              .catch(() => {
                window.dispatchEvent(new CustomEvent('project-list-update'));
              });
          }
        } catch (e) {
          console.error('캐시 무효화 실패:', e);
        }
      }
      
    } catch (e) {
      console.error('계약 거절 오류:', e);
      alert('네트워크 오류가 발생했습니다.');
    }
  }, [currentProject, uid]);
  
  const handleRejectCompleteModalClose = useCallback(() => {
    setShowRejectCompleteModal(false);
    
    const newProjects = projects.filter((_, idx) => idx !== currentIndex);
    setProjects(newProjects);
    cacheSelectedProjects(uid, newProjects);
    
    if (newProjects.length === 0) {
      setCurrentIndex(0);
    } else if (currentIndex >= newProjects.length) {
      setCurrentIndex(newProjects.length - 1);
    }
  }, [projects, currentIndex, uid]);
  
  const shortDate = (d) => {
    if (!d) return '';
    const x = new Date(d);
    return `${x.getMonth() + 1}/${x.getDate()}`;
  };

  const expireLabel = (project) => {
    if (!project?.contractExpireAt) return null;
    const d = calculateDday(project.contractExpireAt);
    const time = new Date(project.contractExpireAt).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false });
    if (d === null) return null;
    if (d < 0) return { text: '응답 기한 지남', red: true };
    if (d === 0) return { text: `응답 기한 오늘 ${time}`, red: true };
    if (d === 1) return { text: `응답 기한 내일 ${time}`, red: true };
    return { text: `응답 기한 D-${d}`, red: d <= 3 };
  };

  if (loading) {
    return (
      <div>
        <div className="mb-page-header">
          <h2 className="mb-page-title">선정된 프로젝트 계약하기</h2>
          <p className="mb-page-description">축하해요! 계약을 수락하면 바로 프로젝트가 시작돼요.</p>
        </div>
        <div className="mb-card" style={{ padding: 40, textAlign: 'center', color: '#6b7686', fontSize: 14, fontWeight: 600 }}>
          선정된 프로젝트를 불러오는 중...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="mb-page-header">
          <h2 className="mb-page-title">선정된 프로젝트 계약하기</h2>
        </div>
        <div className="mb-card" style={{ padding: '48px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#d13b3b', marginBottom: 6 }}>오류가 발생했어요</div>
          <div style={{ fontSize: 14, color: '#6b7686', marginBottom: 18 }}>{error}</div>
          <button type="button" onClick={fetchProjects} className="mb-btn mb-btn-secondary">다시 시도</button>
        </div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div>
        <div className="mb-page-header">
          <h2 className="mb-page-title">선정된 프로젝트 계약하기</h2>
          <p className="mb-page-description">축하해요! 계약을 수락하면 바로 프로젝트가 시작돼요.</p>
        </div>
        <div className="mb-card" style={{ padding: '72px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#3b4658', marginBottom: 6 }}>
            계약 대기 중인 프로젝트가 없어요
          </div>
          <div style={{ fontSize: 14, color: '#6b7686' }}>
            선정되면 이 화면과 알림으로 알려드려요
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-page-header">
        <h2 className="mb-page-title">선정된 프로젝트 계약하기</h2>
        <p className="mb-page-description">축하해요! 계약을 수락하면 바로 프로젝트가 시작돼요.</p>
      </div>

      {projects.map((project, idx) => {
        const expire = expireLabel(project);
        const submitEnd = project.schedule?.submitEnd;
        return (
          <div className="subcard subcard--focus" key={project.applicationId || project.id}>
            <div className="subcard__head">
              <span className="mbh-pcard__logo" style={{ width: 40, height: 40, fontSize: 13 }} aria-hidden="true">
                {project.coverImage
                  ? <img src={project.coverImage} alt="" decoding="async" />
                  : getProductName(project).charAt(0)}
              </span>
              <div className="subcard__who">
                <div className="subcard__name">{getProductName(project)}</div>
                <div className="subcard__sns">
                  {[
                    `NO.${project.projectNumber}`,
                    getServiceTypeText(project.serviceType),
                    getContentTypeText(project.contentType),
                    submitEnd ? `제출 마감 ${shortDate(submitEnd)}` : null,
                  ].filter(Boolean).join(' · ')}
                </div>
              </div>
              {expire && <span className={`deadline${expire.red ? ' deadline--red' : ''}`}>{expire.text}</span>}
            </div>

            <div className="offer__reward" style={{ marginBottom: 14 }}>
              <div><b className="is-mint">{Number(project.rewardPointPerCreator || 0).toLocaleString()}P</b><span>승인 시 보상</span></div>
              <div>
                {project.perPurchaseReward?.use
                  ? <><b className="is-violet">+{Number(project.perPurchaseReward.rewardPerBuyer || 0).toLocaleString()}P</b><span>구매 1명당 (CPS)</span></>
                  : <><b>—</b><span>CPS 없음</span></>}
              </div>
              <div><b>{submitEnd ? shortDate(submitEnd) : '-'}</b><span>결과물 마감</span></div>
            </div>

            {/* 광고주 요청사항 */}
            <div className="reqbox">
              <b>광고주 요청사항</b>
              <div style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'pre-wrap' }}>
                {project.marketerRequest || '(광고주 추가 요청사항 없음)'}
              </div>
              {project.marketerRequest && (
                <button
                  type="button"
                  onClick={() => { setCurrentIndex(idx); setShowRequestModal(true); }}
                  style={{ border: 0, background: 'none', color: '#0a8f62', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, padding: 0, marginTop: 4 }}
                >
                  전체 내용 보기 →
                </button>
              )}
            </div>

            {/* 지원한 SNS 계정 */}
            {Array.isArray(project.snsAccounts) && project.snsAccounts.length > 0 && (
              <div className="mb-info-hint" style={{ marginTop: 0, marginBottom: 12 }}>
                지원 계정: {project.snsAccounts.map((account) => {
                  const colonIdx = account.indexOf(':');
                  const username = colonIdx !== -1 ? normalizeAccountName(account.slice(colonIdx + 1)) : account;
                  return `@${username}`;
                }).join(' · ')}
                {project.manager?.name ? ` · 담당자 ${project.manager.name}` : ''}
              </div>
            )}

            {/* CPS 고객 유입 URL */}
            {project.perPurchaseReward?.use && project.trackingUrl && (
              <div className="linkbox">
                <a
                  href={project.trackingUrl}
                  onClick={(e) => {
                    e.preventDefault();
                    navigator.clipboard.writeText(project.trackingUrl);
                    alert('CPS URL이 클립보드에 복사되었습니다!');
                  }}
                  title="클릭하면 복사돼요"
                >
                  <span>{project.trackingUrl}</span>
                  <b style={{ marginLeft: 'auto', flex: 'none', fontSize: 12 }}>복사</b>
                </a>
              </div>
            )}

            <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="mb-btn mb-btn-secondary"
                onClick={() => { setCurrentIndex(idx); setShowDetailModal(true); }}
              >
                상세보기
              </button>
              <button
                type="button"
                className="mb-btn mb-btn-danger"
                style={{ flex: 1 }}
                onClick={() => { setCurrentIndex(idx); setShowRejectModal(true); }}
              >
                거절하기
              </button>
              <button
                type="button"
                className="mb-btn mb-btn-primary"
                style={{ flex: 2 }}
                onClick={() => { setCurrentIndex(idx); setContractStep(1); }}
              >
                계약 내용 확인하고 수락하기
              </button>
            </div>
          </div>
        );
      })}

      {/* 계약 진행 안내 */}
      <div className="mb-card">
        <h3 className="mb-section-title">계약은 이렇게 진행돼요</h3>
        <div className="checkline">
          <i>1</i>
          <div>
            <b>계약 안내 확인</b>
            <span>제출물·마감일·보상 조건을 확인해요</span>
          </div>
        </div>
        <div className="checkline">
          <i>2</i>
          <div>
            <b>계약 동의</b>
            <span>동의하면 즉시 계약이 체결되고 프로젝트가 시작돼요</span>
          </div>
        </div>
        <div className="checkline">
          <i>3</i>
          <div>
            <b>콘텐츠 제작 · 제출</b>
            <span>마감일 안에 "결과물 제출하기"에서 링크를 제출해요</span>
          </div>
        </div>
        <div className="note note--amber">
          응답 기한이 지나면 선정이 자동 취소되고 다른 지원자에게 기회가 넘어가요.
          (잦은 계약 취소 시 프로젝트 선정률이 낮아져요.)
        </div>
      </div>

      {/* 광고주 요청사항 전체보기 모달 */}
      {showRequestModal && currentProject && (
        <div className="mbm-overlay" onClick={() => setShowRequestModal(false)}>
          <div
            className="mb-card mbm"
            style={{ width: 640, maxWidth: '92vw', margin: 0, padding: 28, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-section-title" style={{ marginBottom: 14 }}>광고주 요청사항</h3>
            <div className="reqbox" style={{ marginBottom: 0, overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
              {currentProject.marketerRequest}
            </div>
            <div className="mbm-foot" style={{ marginTop: 16 }}>
              <button type="button" className="mb-btn mb-btn-primary" onClick={() => setShowRequestModal(false)}>확인</button>
            </div>
          </div>
        </div>
      )}

      <ProjectDetailModal
        open={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        project={currentProject}
      />
      <ContractRequestModal open={contractStep === 1} onClose={() => setContractStep(0)} onNext={() => setContractStep(2)} project={currentProject} />
      <ContractGuideModal open={contractStep === 2} onClose={() => setContractStep(0)} onNext={() => setContractStep(3)} project={currentProject} />
      <ContractConfirmModal open={contractStep === 3} onClose={() => setContractStep(0)} onConfirm={handleAcceptContract} project={currentProject} />
      <ContractCompleteModal open={showCompleteModal} onClose={handleCompleteModalClose} />
      <ContractRejectModal open={showRejectModal} onClose={() => setShowRejectModal(false)} onConfirm={handleRejectContract} project={currentProject} />
      <ContractRejectCompleteModal open={showRejectCompleteModal} onClose={handleRejectCompleteModalClose} />
    </div>
  );
}

export default InfluencerProjectContract;
